import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, AlertCircle, Home, Calendar, Package, Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { safeFetchCompat as fetch } from '../utils/safeFetch';

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [bookingData, setBookingData] = useState(null);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');
  const bookingRef = searchParams.get('ref');
  const serviceType = searchParams.get('service'); // 'waste' or 'delivery'

  useEffect(() => {
    if (bookingRef) {
      // Subscription booking or direct booking - no payment needed
      fetchBookingByRef(bookingRef);
    } else if (sessionId) {
      // Paid booking - verify payment
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setError('No booking reference found');
    }
  }, [sessionId, bookingRef]);

  const fetchBookingByRef = async (ref) => {
    try {
      // For subscription bookings, the booking is already confirmed
      // We just need to show the success message
      setStatus('success');
      setBookingData({
        booking_ref: ref,
        service_type: serviceType || 'waste',
        is_subscription: true
      });
    } catch (err) {
      console.error('Error fetching booking:', err);
      setStatus('error');
      setError('Could not find your booking. Please contact support.');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setBookingData({
          ...data,
          is_subscription: false
        });
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      // Retry a few times before giving up
      if (attempts < 3) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError('Error verifying payment. Please contact support.');
      }
    }
  };

  return (
    <main className="bg-[#F8FAFC] min-h-screen py-12">
      <div className="max-w-xl mx-auto px-4">
        {status === 'loading' && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-12 text-center">
              <Loader2 className="w-16 h-16 animate-spin text-[#0D9488] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#111111]">
                {sessionId ? 'Verifying Payment...' : 'Confirming Booking...'}
              </h2>
              <p className="text-[#64748B] mt-3">
                Please wait while we confirm your {sessionId ? 'payment' : 'booking'}
              </p>
            </CardContent>
          </Card>
        )}

        {status === 'success' && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl font-bold text-[#111111]">Booking Confirmed!</h2>
              
              {bookingData?.is_subscription ? (
                // Subscription booking - no payment message
                <p className="text-[#64748B] mt-3">
                  Your booking has been submitted successfully as part of your subscription.
                </p>
              ) : (
                // Paid booking
                <p className="text-[#64748B] mt-3">
                  Your payment has been processed successfully.
                </p>
              )}
              
              {/* Booking Reference */}
              <div className="bg-[#F8FAFC] rounded-xl p-4 mt-6 text-left space-y-3">
                {bookingData?.booking_ref && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#64748B]">Booking Reference</span>
                    <span className="font-mono font-semibold text-[#0D9488]">
                      {bookingData.booking_ref}
                    </span>
                  </div>
                )}
                
                {bookingData?.service_type && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#64748B]">Service</span>
                    <span className="font-medium text-[#111111] flex items-center gap-2">
                      {bookingData.service_type === 'waste' || bookingData.service_type === 'pickup' ? (
                        <><Package className="w-4 h-4" /> Waste Collection</>
                      ) : (
                        <><Truck className="w-4 h-4" /> Materials Delivery</>
                      )}
                    </span>
                  </div>
                )}
                
                {bookingData?.is_subscription && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#64748B]">Payment</span>
                    <span className="font-medium text-[#22C55E]">
                      Subscription (No charge)
                    </span>
                  </div>
                )}
                
                {!bookingData?.is_subscription && bookingData?.amount && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#64748B]">Amount Paid</span>
                    <span className="font-semibold text-[#111111]">
                      ${bookingData.amount?.toFixed(2)} {bookingData.currency?.toUpperCase() || 'AUD'}
                    </span>
                  </div>
                )}
              </div>

              {/* What happens next */}
              <div className="bg-[#F0FDFA] rounded-xl p-4 mt-6 text-left">
                <h4 className="font-medium text-[#0F766E] mb-2">What happens next?</h4>
                <ul className="text-sm text-[#134E4A] space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>We'll contact your customer to coordinate the {bookingData?.service_type === 'delivery' ? 'delivery' : 'collection'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>You'll receive SMS updates on the job status</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Photo confirmation will be sent when complete</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 space-y-3">
                <Link to="/">
                  <Button className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
                <Link to="/book/waste">
                  <Button variant="outline" className="w-full border-[#E2E8F0] h-12">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Another Collection
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {status === 'error' && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#111111]">Something Went Wrong</h2>
              <p className="text-[#64748B] mt-3">{error}</p>

              <div className="bg-[#FEF2F2] rounded-xl p-4 mt-6">
                <p className="text-sm text-[#991B1B]">
                  If you believe your booking was successful, please contact us at{' '}
                  <a href="tel:+61407502587" className="font-medium underline">+61 407 502 587</a> or{' '}
                  <a href="mailto:hello@transa.com.au" className="font-medium underline">hello@transa.com.au</a>
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <Link to="/book/waste">
                  <Button className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12">
                    Try Again
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full border-[#E2E8F0] h-12">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
};

export default BookingSuccess;
